import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useCreateStockAdjustmentMutation } from "../../stockApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateAdjustment } from "../../useStockPermissions";
import LineItemsEditor, { emptyRow } from "../../components/LineItemsEditor";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";

// The only Stock document with no rate/VAT/monetary value at all - a pure
// quantity correction (INCREASE/DECREASE) to book stock. Balance only moves
// on a separate user's approval (server-enforced), not at creation.
const columns = { showRate: false, showVat: false, showRemarks: false, showReason: true, showMaxQty: false };
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const StockAdjustmentForm = () => {
  const navigate = useNavigate();
  const canCreate = useCanCreateAdjustment();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createStockAdjustment, { isLoading: isSaving }] = useCreateStockAdjustmentMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [adjustmentType, setAdjustmentType] = useState("INCREASE");
  const [adjustmentDate, setAdjustmentDate] = useState(toDateInput(new Date()));
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState([emptyRow()]);

  const readOnly = !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    if (!reason.trim()) return toast.error("A reason is required");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");

    const body = {
      branchId: Number(branchId),
      adjustmentDate: new Date(adjustmentDate).toISOString(),
      adjustmentType,
      reason: reason.trim(),
      lines: validLines.map((l) => ({
        productCode: Number(l.productCode),
        productName: l.productName,
        adjustedQty: Number(l.qty),
        unit: l.unit || null,
        reason: l.reason || null,
      })),
    };

    try {
      const created = await createStockAdjustment(body).unwrap();
      toast.success("Stock adjustment submitted for approval");
      navigate(paths.stockAdjustmentDetail(created.recordGuid), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not submit the adjustment");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockAdjustments} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Adjustments</Link>
              <h4>New Stock Adjustment</h4>
            </div>
          </div>
        </div>

        {readOnly && <div className="alert alert-info">You have read-only access. Submitting an adjustment requires Stock.CreateAdjustment.</div>}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={readOnly}>
                  <option value="">--Select--</option>
                  {availableBranches.map((b) => (
                    <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Type</label>
                <select className="form-control" value={adjustmentType} onChange={(e) => setAdjustmentType(e.target.value)} disabled={readOnly}>
                  <option value="INCREASE">Increase</option>
                  <option value="DECREASE">Decrease</option>
                </select>
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Date</label>
                <input type="date" className="form-control" value={adjustmentDate} onChange={(e) => setAdjustmentDate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Reason (required)</label>
                <input type="text" className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} disabled={readOnly} />
              </div>
            </div>

            <hr />
            <h5 className="mb-3">Lines ({adjustmentType === "INCREASE" ? "quantity being added" : "quantity being removed"})</h5>
            <LineItemsEditor branchId={branchId ? Number(branchId) : undefined} rows={lines} onChange={setLines} columns={columns} disabled={readOnly} />

            {!readOnly && (
              <div className="d-flex gap-2 mt-3">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockAdjustments)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentForm;
